/**
 * Cookie consent manager.
 *
 * Stores the visitor's choice in localStorage and, on accept, dynamically
 * loads Google Tag Manager, GA4 and (path-gated) Microsoft Clarity. These
 * trackers are NOT loaded at any other time - this is required for UK GDPR
 * / PECR compliance because they are non-essential analytics cookies.
 *
 * Clarity is additionally gated by `isClarityAllowedPath` so that it never
 * loads, initialises, or records on admin/account/auth/checkout/assessment
 * routes - even for users who have accepted analytics cookies.
 */

import { isClarityAllowedPath } from "./clarityPath";

const STORAGE_KEY = "tk_cookie_consent_v1";
const GTM_ID = "GTM-T5PC45KR";
const GA4_ID = "G-R0DKD23V85";
const CLARITY_ID = "we9r9dondu";

export type ConsentStatus = "accepted" | "rejected" | null;

let baseLoaded = false; // GA4 + GTM
let clarityLoaded = false;
let clarityActive = false;

export function getConsent(): ConsentStatus {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "accepted" || v === "rejected") return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function setConsent(status: "accepted" | "rejected") {
  try {
    window.localStorage.setItem(STORAGE_KEY, status);
  } catch {
    /* ignore */
  }
  if (status === "accepted") {
    scheduleLoad();
  } else {
    revokeClarityConsent();
  }
}

export function loadTrackersIfConsented() {
  if (getConsent() === "accepted") scheduleLoad();
}

/**
 * Called on every route change. Loads or revokes Clarity based on whether
 * the current pathname is in the public allowlist. GA4/GTM are unaffected.
 */
export function syncClarityForRoute(pathname: string) {
  if (getConsent() !== "accepted") return;
  const allowed = isClarityAllowedPath(pathname);
  if (allowed) {
    loadClarity();
  } else if (clarityActive) {
    revokeClarityConsent();
  }
}

function scheduleLoad() {
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  const run = () => {
    loadBaseTrackers();
    if (typeof window !== "undefined" && isClarityAllowedPath(window.location.pathname)) {
      loadClarity();
    }
  };
  if (ric) ric(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

function loadBaseTrackers() {
  if (baseLoaded) return;
  baseLoaded = true;

  // Google Tag Manager
  (function (w: any, d: Document, s: string, l: string, i: string) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s) as HTMLScriptElement;
    const dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode?.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);

  // Google Analytics 4
  const ga = document.createElement("script");
  ga.async = true;
  ga.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(ga);
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA4_ID);
}

function loadClarity() {
  if (!clarityLoaded) {
    clarityLoaded = true;
    // Inject Clarity script exactly once for the SPA lifetime.
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] =
        c[a] ||
        function (...args: any[]) {
          (c[a].q = c[a].q || []).push(args);
        };
      const t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.src = "https://www.clarity.ms/tag/" + i;
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_ID);
  }
  if (!clarityActive) {
    try {
      (window as any).clarity?.("consent", true);
    } catch {
      /* ignore */
    }
    clarityActive = true;
  }
}

function revokeClarityConsent() {
  if (!clarityLoaded) return;
  try {
    (window as any).clarity?.("consent", false);
    // Stop further session recording on this page.
    (window as any).clarity?.("stop");
  } catch {
    /* ignore */
  }
  clarityActive = false;
}

/**
 * Optional safe custom tag for Clarity. Use ONLY for non-sensitive
 * structural signals (e.g. assessment_step=3). Never pass answer text,
 * user IDs, emails, or any free-form user input. No-ops on private routes.
 */
export function clarityTag(key: string, value: string | number) {
  if (getConsent() !== "accepted") return;
  if (typeof window === "undefined") return;
  if (!isClarityAllowedPath(window.location.pathname)) return;
  try {
    (window as any).clarity?.("set", key, String(value));
  } catch {
    /* ignore */
  }
}
